import { useState, useEffect, useCallback } from 'react'
import axios from 'axios'
import { supabase } from '../lib/supabase'

export default function useNotifications(user) {
  const [notifications, setNotifications] = useState([])
  const [unread, setUnread] = useState(0)

  const getHeaders = useCallback(async () => {
    const { data: { session } } = await supabase.auth.getSession()
    // Guard on access_token specifically — getSession() can return a session
    // object without a populated access_token in the window between sign-in
    // and SDK cache sync. Caller checks for null and skips the request.
    return session?.access_token ? { Authorization: `Bearer ${session.access_token}` } : null
  }, [])

  const fetchNotifications = useCallback(async () => {
    if (!user) return
    const headers = await getHeaders()
    if (!headers) return
    try {
      const { data } = await axios.get('/api/notifications', { headers })
      setNotifications(data)
      setUnread(data.filter(n => !n.read).length)
    } catch (err) { console.error('[useNotifications] Failed to fetch notifications:', err) }
  }, [user, getHeaders])

  useEffect(() => {
    fetchNotifications()
    const interval = setInterval(() => {
      if (document.visibilityState === 'visible') fetchNotifications()
    }, 30000)
    return () => clearInterval(interval)
  }, [fetchNotifications])

  const markAllRead = useCallback(async () => {
    const headers = await getHeaders()
    if (!headers) return
    try {
      await axios.patch('/api/notifications/read-all', {}, { headers })
      setNotifications(prev => prev.map(n => ({ ...n, read: true })))
      setUnread(0)
    } catch (err) { console.error('[useNotifications] Failed to mark all notifications as read:', err) }
  }, [getHeaders])

  const markRead = useCallback(async (id) => {
    const headers = await getHeaders()
    if (!headers) return
    try {
      await axios.patch(`/api/notifications/${id}/read`, {}, { headers })
      setNotifications(prev => prev.map(n => n.id === id ? { ...n, read: true } : n))
      setUnread(prev => Math.max(0, prev - 1))
    } catch (err) { console.error('[useNotifications] Failed to mark notification as read:', err) }
  }, [getHeaders])

  const clearAll = useCallback(async () => {
    const headers = await getHeaders()
    if (!headers) return
    try {
      await axios.delete('/api/notifications/clear', { headers })
      setNotifications([])
      setUnread(0)
    } catch (err) { console.error('[useNotifications] Failed to clear notifications:', err) }
  }, [getHeaders])

  return { notifications, unread, markAllRead, markRead, clearAll, refresh: fetchNotifications }
}
